import { BaseLayout } from '@components/Layout';
import { t, Trans } from '@lingui/macro';
import {
  Button2,
  CheckCircle,
  FlowContainer,
  Link2,
  MiloFull,
  ReactFC,
  Skeleton,
  useGraphqlClient,
  useMutation,
  useQuery,
} from '@milo/react';
import { MailingList } from '@milo/types';
import { GetServerSideProps } from 'next';
import React, { FormEventHandler, useState } from 'react';

type NewsletterUnsubscribeFormProps = {
  email: string;
};

const NewsletterUnsubscribeForm: ReactFC<NewsletterUnsubscribeFormProps> = (props) => {
  const email = props?.email;
  const [unsubs, setUnsubs] = useState<string[]>([]);
  const [done, setDone] = useState<boolean>(false);
  const { client } = useGraphqlClient();

  const { data: mailingLists, isLoading: mailingListsLoading } = useQuery(['MailingLists'], async () => {
    const response = await client.request(/* GraphQL */ `
      query NewsletterGetMailingLists {
        NewsletterGetMailingLists {
          id
          name
          description
        }
      }
    `);
    const results = response?.NewsletterGetMailingLists;
    return results;
  });

  const { mutate: unsubscribe, isLoading: unsubMutationLoading } = useMutation(async () => {
    await client.request(
      /* GraphQL */ `
        mutation NewsletterUnsubscribeMember($input: NewsletterUnsubscribeMemberInput!) {
          NewsletterUnsubscribeMember(input: $input) {
            success
          }
        }
      `,
      {
        input: {
          email,
          listIds: unsubs,
        },
      }
    );

    setDone(true);
  });

  const submittable = unsubs.length > 0 && !mailingListsLoading;
  const loading = mailingListsLoading || unsubMutationLoading;

  const submit: FormEventHandler = async (e) => {
    e.preventDefault();
    await unsubscribe();
  };

  const clickUnsubAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (checked) {
      return setUnsubs(['all']);
    }

    return setUnsubs((unsubs) => [...unsubs].filter((val) => val !== 'all'));
  };

  const clickIndividual = (id: string) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setUnsubs((unsubs) => {
        if (unsubs.includes('all')) {
          return [...mailingLists.filter((m: MailingList) => m.id !== id).map((m) => m.id)];
        }

        if (unsubs.includes(id)) {
          return unsubs.filter((u) => {
            return u !== 'all' && u !== id;
          });
        }

        return [...unsubs, id];
      });
    };
  };

  if (done) {
    return (
      <div className="flex flex-col items-center p-4">
        <p className="mx-auto mb-4 flex text-2xl">
          <Trans>Préférences enregistrées, merci</Trans>
          <CheckCircle fill="success" width={33} height={33} className="ml-2" />
        </p>
        <Link2 href="/experiences" className="text-lg underline">
          <Trans>Aller aux expériences</Trans>
        </Link2>
      </div>
    );
  }

  const content = (() => {
    if (mailingListsLoading) {
      return (
        <div>
          {[1, 2, 3].map((i) => (
            <div className="mb-3 flex justify-between" key={`skeleton-${i}`}>
              <Skeleton className="h-6 w-10/12 rounded-md" />
              <Skeleton className="h-6 w-6 rounded-md" />
            </div>
          ))}
        </div>
      );
    }

    return mailingLists?.map((mailingList: MailingList) => (
      <label htmlFor={mailingList.id} className="mb-2 flex w-full cursor-pointer justify-between" key={mailingList.id}>
        {mailingList.name}
        <input
          onChange={clickIndividual(mailingList.id)}
          className="h-5 w-5"
          id={mailingList.id}
          type="checkbox"
          checked={!unsubs.includes(mailingList.id) && !unsubs.includes('all')}
        />
      </label>
    ));
  })();

  return (
    <div className="p-4">
      <MiloFull className="mx-auto mb-5 w-40" />
      <form className="mx-auto max-w-2xl text-center" onSubmit={submit}>
        <h1 className="mb-2 text-3xl font-semibold text-gray-700">Gestion de l'abonnement aux infolettres</h1>
        <p className="mb-6 text-xl">Choisissez le contenu que vous souhaitez recevoir:</p>
        <div className="mx-auto mb-4 flex max-w-md flex-col items-start">
          <div className="mb-8 w-full">{content}</div>
          <label htmlFor={'all'} className="mb-5 flex  w-full cursor-pointer justify-between">
            Se désabonner de toutes les infolettres
            <input
              className="h-5 w-5"
              id={'all'}
              type="checkbox"
              checked={unsubs.includes('all')}
              onChange={clickUnsubAll}
            />
          </label>
          <Button2 block type="submit" disabled={!submittable} loading={loading}>
            <Trans>Soumettre</Trans>
          </Button2>
        </div>
      </form>
    </div>
  );
};

export default function NewsletterUnsubscribe({ email }: { email: string }) {
  return (
    <BaseLayout showBanner={false} showNavigation={false} showFooter={false} transparentNavigation>
      <FlowContainer>
        <NewsletterUnsubscribeForm email={email} />
      </FlowContainer>
    </BaseLayout>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const email = context?.query?.email;
  return {
    props: {
      email,
      seo: {
        title: t`Infolettre M ta région`,
        description: t`Désinscription à l'infolettre M ta région`,
      },
    },
  };
};
